"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod/v3";

import { Logo } from "@/components/Logo";
import { TermsAgreement } from "@/components/TermsAgreement";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";

const formSchema = z.object({
	name: z.string().min(2, { message: "Please enter your name" }),
	email: z.string().email({ message: "Please enter a valid email address" }),
	password: z
		.string()
		.min(12, { message: "Password must be at least 12 characters" }),
	acceptTerms: z.boolean().refine((value) => value, {
		message: "Agree to the Terms of Use and Privacy Notice to continue",
	}),
});

function SignupForm() {
	const queryClient = useQueryClient();
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const { signUp } = useAuth();

	useUser({ redirectTo: "/onboarding", redirectWhen: "authenticated" });

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: { name: "", email: "", password: "", acceptTerms: false },
	});

	const acceptedTerms = useWatch({
		control: form.control,
		name: "acceptTerms",
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsLoading(true);
		try {
			const res = await signUp.email({
				name: values.name,
				email: values.email,
				password: values.password,
			});
			if (res.error) {
				toast.error(res.error.message ?? "Failed to create your account");
				return;
			}
			queryClient.clear();
			toast.success(
				"Welcome aboard. Check your inbox to verify your email — claiming a carrier requires it.",
			);
			router.push("/onboarding");
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<div className="radar-grid flex min-h-screen items-center justify-center px-4 py-10">
			<div className="w-full max-w-md">
				<Link href="/" className="mb-8 flex items-center justify-center gap-2">
					<Logo />
					<span className="font-display text-lg font-black tracking-tight">
						AIRSIDE
					</span>
				</Link>
				<div className="border-border bg-card rounded-xl border p-8 shadow-2xl">
					<p className="text-primary mb-1 font-mono text-[0.65rem] tracking-[0.3em] uppercase">
						New carrier registration
					</p>
					<h1 className="font-display text-2xl font-black tracking-tight">
						Claim your carrier code
					</h1>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="mt-6 space-y-4"
						>
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Name</FormLabel>
										<FormControl>
											<Input autoComplete="name" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Work email</FormLabel>
										<FormControl>
											<Input
												type="email"
												autoComplete="email"
												placeholder="ops@yourprovider.ai"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											Use an address on your provider&apos;s domain — it&apos;s
											what unlocks your carrier claim.
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Password</FormLabel>
										<FormControl>
											<Input
												type="password"
												autoComplete="new-password"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="acceptTerms"
								render={({ field }) => (
									<FormItem>
										<TermsAgreement
											id="signup-accept-terms"
											checked={field.value}
											onChange={field.onChange}
										/>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								className="w-full font-semibold"
								disabled={isLoading || !acceptedTerms}
							>
								{isLoading ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									"Create account"
								)}
							</Button>
						</form>
					</Form>
					<p className="text-muted-foreground mt-6 text-center text-sm">
						Already flying with us?{" "}
						<Link href="/login" className="text-primary hover:underline">
							Sign in
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}

export default function SignupPage() {
	return <SignupForm />;
}
